import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-hot-toast";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../utils/api";
import {
  CalendarIcon,
  UsersIcon,
  ExclamationTriangleIcon,
  AdjustmentsHorizontalIcon,
  MapPinIcon,
  ChartBarIcon,
  TrophyIcon,
  ArrowPathIcon,
  ChevronDownIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";

const formatISO = (dateObj) => {
  const d = new Date(dateObj);
  d.setHours(0, 0, 0, 0);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const formatRangeLabel = (range) => {
  if (!range?.inizio || !range?.fine) return "Intervallo non definito";
  const [startY, startM, startD] = range.inizio.split("-");
  const [endY, endM, endD] = range.fine.split("-");
  return `${startD}/${startM}/${startY} → ${endD}/${endM}/${endY}`;
};

const defaultOverview = {
  assegnazioni_totali: 0,
  disponibilita_totali: 0,
  esperienze_totali: 0,
  volontari_conteggiati: 0,
};

const presets = [
  { id: "1m", label: "Ultimo mese", months: 1 },
  { id: "3m", label: "Ultimi 3 mesi", months: 3 },
  { id: "6m", label: "Ultimi 6 mesi", months: 6 },
  { id: "12m", label: "Ultimo anno", months: 12 },
];

const CronologiaStatistiche = () => {
  const { activeCongregazione, user } = useAuth();
  const isAuthorized =
    user?.ruolo === "admin" || user?.ruolo === "super_admin";

  const computePresetRange = useCallback((months) => {
    // Intervallo a mesi interi: dal primo giorno del mese di "months" fa
    // fino all'ultimo giorno del mese corrente.
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(today.getFullYear(), today.getMonth() + 1, 0); // fine mese corrente
    const start = new Date(end.getFullYear(), end.getMonth() - (months - 1), 1); // inizio mese (months-1) fa
    return { start: formatISO(start), end: formatISO(end) };
  }, []);

  const defaultRange = computePresetRange(3);

  const [filters, setFilters] = useState({
    dataInizio: defaultRange.start,
    dataFine: defaultRange.end,
    postazioneId: "",
  });
  const [selectedPreset, setSelectedPreset] = useState("3m");
  const [loading, setLoading] = useState(false);
  const [postazioni, setPostazioni] = useState([]);
  const [showGrid, setShowGrid] = useState(false);
  const [expandedCards, setExpandedCards] = useState({
    attivi: false,
    disponibili: false,
    menoAttivi: false,
  });
  const [stats, setStats] = useState({
    overview: defaultOverview,
    postazioniEsperienze: [],
    proclamatoriAttivi: [],
    proclamatoriDisponibili: [],
    proclamatoriMenoAttivi: [],
    volontari: [],
    range: { inizio: defaultRange.start, fine: defaultRange.end },
  });

  const loadPostazioni = useCallback(async () => {
    if (!activeCongregazione?.id) return;
    try {
      const response = await api.get(
        `/postazioni?congregazione_id=${activeCongregazione.id}`
      );
      setPostazioni(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Errore nel caricamento delle postazioni:", error);
      toast.error("Impossibile caricare le postazioni per il filtro.");
    }
  }, [activeCongregazione?.id]);

  const buildQueryParams = (state) => {
    const params = new URLSearchParams();
    if (state.dataInizio) params.set("data_inizio", state.dataInizio);
    if (state.dataFine) params.set("data_fine", state.dataFine);
    if (state.postazioneId)
      params.set("postazione_id", parseInt(state.postazioneId, 10));
    return params.toString();
  };

  const loadStatistiche = useCallback(
    async (overrideFilters) => {
      if (!activeCongregazione?.id || !isAuthorized) return;
      const activeFilters = overrideFilters || filters;
      setLoading(true);
      try {
        const query = buildQueryParams(activeFilters);
        const response = await api.get(
          query ? `/cronologia/statistiche?${query}` : "/cronologia/statistiche"
        );
        setStats({
          overview: response.data?.overview || defaultOverview,
          postazioniEsperienze: response.data?.postazioniEsperienze || [],
          proclamatoriAttivi: response.data?.proclamatoriAttivi || [],
          proclamatoriDisponibili: response.data?.proclamatoriDisponibili || [],
          proclamatoriMenoAttivi: response.data?.proclamatoriMenoAttivi || [],
          volontari: response.data?.volontari || [],
          range:
            response.data?.range || {
              inizio: activeFilters.dataInizio,
              fine: activeFilters.dataFine,
            },
        });
      } catch (error) {
        console.error("Errore statistiche cronologia:", error);
        toast.error(
          error.response?.data?.message ||
            "Impossibile recuperare le statistiche"
        );
      } finally {
        setLoading(false);
      }
    },
    [activeCongregazione?.id, filters, isAuthorized]
  );

  useEffect(() => {
    if (!activeCongregazione?.id || !isAuthorized) return;
    loadPostazioni();
  }, [activeCongregazione?.id, isAuthorized, loadPostazioni]);

  useEffect(() => {
    if (!activeCongregazione?.id) return;
    const range = computePresetRange(3);
    setSelectedPreset("3m");
    setFilters({
      dataInizio: range.start,
      dataFine: range.end,
      postazioneId: "",
    });
    setStats((prev) => ({
      ...prev,
      range: { inizio: range.start, fine: range.end },
    }));
  }, [activeCongregazione?.id, computePresetRange]);

  useEffect(() => {
    if (!activeCongregazione?.id || !isAuthorized) return;
    loadStatistiche(filters);
  }, [
    activeCongregazione?.id,
    filters,
    filters.dataFine,
    filters.dataInizio,
    filters.postazioneId,
    isAuthorized,
    loadStatistiche,
  ]);

  const handlePreset = (preset) => {
    const range = computePresetRange(preset.months);
    const nextFilters = {
      ...filters,
      dataInizio: range.start,
      dataFine: range.end,
    };
    setSelectedPreset(preset.id);
    setFilters(nextFilters);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const toggleCard = (key) => {
    setExpandedCards((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  if (!isAuthorized) {
    return (
      <div className="p-6">
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 flex items-start gap-3 text-red-800">
          <ExclamationTriangleIcon className="h-6 w-6 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold">Accesso limitato</h2>
            <p className="text-sm">
              Le statistiche sono disponibili solo per admin e super admin.
            </p>
          </div>
        </div>
      </div>
    );
  }

  if (!activeCongregazione?.id) {
    return (
      <div className="p-6">
        <div className="bg-white rounded-xl shadow-sm border border-amber-200 p-6 flex items-start gap-3 text-amber-800">
          <ExclamationTriangleIcon className="h-6 w-6 mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold">
              Seleziona una congregazione
            </h2>
            <p className="text-sm">
              Scegli una congregazione attiva per consultare le statistiche.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const summaryCards = [
    {
      title: "Turni assegnati",
      value: stats.overview.assegnazioni_totali,
      icon: <CalendarIcon className="h-6 w-6 text-blue-600" />,
      color: "bg-blue-50 border-blue-100",
    },
    {
      title: "Disponibilità inserite",
      value: stats.overview.disponibilita_totali,
      icon: <UsersIcon className="h-6 w-6 text-indigo-600" />,
      color: "bg-indigo-50 border-indigo-100",
    },
    {
      title: "Esperienze registrate",
      value: stats.overview.esperienze_totali,
      icon: <ChartBarIcon className="h-6 w-6 text-emerald-600" />,
      color: "bg-emerald-50 border-emerald-100",
    },
    {
      title: "Proclamatori conteggiati",
      value: stats.overview.volontari_conteggiati,
      icon: <UsersIcon className="h-6 w-6 text-orange-600" />,
      color: "bg-orange-50 border-orange-100",
    },
  ];

  const renderRanking = (items, valueKey, emptyLabel, expandedKey) => {
    if (!items || items.length === 0) {
      return (
        <p className="text-sm text-gray-500">
          {emptyLabel || "Nessun dato nel periodo selezionato."}
        </p>
      );
    }
    const isExpanded = expandedKey ? expandedCards[expandedKey] : false;
    const visibleItems = isExpanded ? items : items.slice(0, 8);
    return (
      <div
        className={`relative h-80 ${isExpanded ? "overflow-y-auto" : "overflow-hidden"}`}
      >
        <ol className="space-y-3">
          {visibleItems.map((item, index) => (
            <li
              key={item.id || `${item.nome}-${index}`}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-3">
                <span className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-semibold">
                  {index + 1}
                </span>
                <div>
                  <p className="font-semibold text-gray-900">
                    {item.nome} {item.cognome}
                  </p>
                  {item.telefono && (
                    <p className="text-xs text-gray-500">{item.telefono}</p>
                  )}
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {item[valueKey] ?? 0}
                </p>
                <p className="text-xs text-gray-500">
                  {valueKey === "disponibilita" ? "disponibilità" : "turni"}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Statistiche Registro
          </h1>
          <p className="text-gray-600 mt-1">
            Analisi assegnazioni, disponibilità e esperienze per congregazione.
          </p>
          <div className="mt-2 inline-flex items-center gap-2 text-sm text-blue-700 bg-blue-50 border border-blue-100 rounded-md px-3 py-2">
            <CalendarIcon className="h-4 w-4" />
            <span>Intervallo massimo: ultimi 12 mesi.</span>
          </div>
        </div>
        <div className="text-sm text-gray-600 bg-white border border-gray-200 rounded-lg px-4 py-2 shadow-sm">
          Range attivo:{" "}
          <span className="font-semibold text-gray-900">
            {formatRangeLabel(stats.range)}
          </span>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex items-center gap-2 text-gray-900 font-semibold">
            <AdjustmentsHorizontalIcon className="h-5 w-5" />
            Filtri
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <ArrowPathIcon className="h-4 w-4" />
            Aggiorna automatico ad ogni modifica
          </div>
        </div>
        <div className="accent-line mt-3" />
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 mt-4">
          <div className="flex flex-wrap gap-2">
            {presets.map((preset) => (
              <button
                key={preset.id}
                onClick={() => handlePreset(preset)}
                className={`px-3 py-2 rounded-md text-sm border ${
                  selectedPreset === preset.id
                    ? "bg-primary-50 text-primary-700 border-primary-200"
                    : "bg-gray-50 text-gray-700 border-gray-200 hover:border-primary-200"
                }`}
              >
                {preset.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <div className="flex-1">
              <label className="form-label">Data inizio</label>
              <input
                type="date"
                className="form-input"
                value={filters.dataInizio}
                max={filters.dataFine || undefined}
                onChange={(e) => handleFilterChange("dataInizio", e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <CalendarIcon className="h-5 w-5 text-gray-500" />
            <div className="flex-1">
              <label className="form-label">Data fine</label>
              <input
                type="date"
                className="form-input"
                value={filters.dataFine}
                min={filters.dataInizio || undefined}
                onChange={(e) => handleFilterChange("dataFine", e.target.value)}
              />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <MapPinIcon className="h-5 w-5 text-gray-500" />
            <div className="flex-1">
              <label className="form-label">Postazione</label>
              <select
                className="form-input"
                value={filters.postazioneId}
                onChange={(e) => handleFilterChange("postazioneId", e.target.value)}
              >
                <option value="">Tutte le postazioni</option>
                {postazioni.map((postazione) => (
                  <option key={postazione.id} value={postazione.id}>
                    {postazione.luogo}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {summaryCards.map((card) => (
          <div
            key={card.title}
            className={`relative bg-white border ${card.color} rounded-lg p-4 shadow-sm`}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-white border border-gray-100">
                  {card.icon}
                </div>
                <div>
                  <p className="text-sm text-gray-600">{card.title}</p>
                  <p className="text-2xl font-semibold text-gray-900">
                    {card.value}
                  </p>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="relative bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Postazioni con più esperienze
              </h3>
              <p className="text-sm text-gray-500">
                Ordinate per numero di esperienze registrate
              </p>
            </div>
          </div>
          {stats.postazioniEsperienze.length === 0 ? (
            <p className="text-sm text-gray-500">
              Nessuna esperienza nel periodo selezionato.
            </p>
          ) : (
            <ol className="space-y-3">
              {[...stats.postazioniEsperienze]
                .sort(
                  (a, b) =>
                    b.esperienze_totali - a.esperienze_totali ||
                    (a.luogo || "").localeCompare(b.luogo || "")
                )
                .map((postazione, index) => (
                  <li
                    key={postazione.id || `${postazione.luogo}-${index}`}
                    className="flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <span className="h-8 w-8 rounded-full bg-primary-50 text-primary-700 flex items-center justify-center text-sm font-semibold">
                        {index + 1}
                      </span>
                      <div>
                        <p className="font-semibold text-gray-900">
                          {postazione.luogo || "Postazione rimossa"}
                        </p>
                        {postazione.indirizzo && (
                          <p className="text-xs text-gray-500">
                            {postazione.indirizzo}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm font-semibold text-gray-900">
                        {postazione.esperienze_totali}
                      </p>
                      <p className="text-xs text-gray-500">esperienze</p>
                    </div>
                  </li>
                ))}
            </ol>
          )}
        </div>

        <div className="relative bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Proclamatori più attivi
              </h3>
              <p className="text-sm text-gray-500">
                Maggior numero di turni assegnati
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="text-sm text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1.5 rounded hover:bg-primary-100"
                onClick={() => toggleCard("attivi")}
              >
                {expandedCards.attivi ? "Comprimi" : "Espandi"}
              </button>
              <TrophyIcon className="h-6 w-6 text-amber-500" />
            </div>
          </div>
          {renderRanking(
            stats.proclamatoriAttivi,
            "assegnazioni",
            "Nessun turno assegnato nel periodo.",
            "attivi"
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
        <div className="relative bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Proclamatori più disponibili
              </h3>
              <p className="text-sm text-gray-500">
                Chi ha inserito più disponibilità nel periodo
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="text-sm text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1.5 rounded hover:bg-primary-100"
                onClick={() => toggleCard("disponibili")}
              >
                {expandedCards.disponibili ? "Comprimi" : "Espandi"}
              </button>
              <UsersIcon className="h-6 w-6 text-sky-500" />
            </div>
          </div>
          {renderRanking(
            stats.proclamatoriDisponibili,
            "disponibilita",
            "Nessuna disponibilità inserita nel periodo.",
            "disponibili"
          )}
        </div>

        <div className="relative bg-white border border-gray-200 rounded-lg p-6 shadow-sm">
          <span className="accent-bar top-4 bottom-4" aria-hidden="true" />
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Proclamatori meno attivi
              </h3>
              <p className="text-sm text-gray-500">
                Ordinati per minor numero di disponibilità
              </p>
            </div>
            <div className="flex items-center gap-3">
              <button
                className="text-sm text-primary-700 bg-primary-50 border border-primary-200 px-2.5 py-1.5 rounded hover:bg-primary-100"
                onClick={() => toggleCard("menoAttivi")}
              >
                {expandedCards.menoAttivi ? "Comprimi" : "Espandi"}
              </button>
              <ChartBarIcon className="h-6 w-6 text-rose-500" />
            </div>
          </div>
          {renderRanking(
            stats.proclamatoriMenoAttivi,
            "disponibilita",
            "Tutti i proclamatori hanno zero disponibilità nel periodo.",
            "menoAttivi"
          )}
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
        <div className="p-5 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <UsersIcon className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                Griglia completa proclamatori
              </h3>
              <p className="text-sm text-gray-500">
                Numero di assegnazioni e disponibilità nel periodo selezionato
              </p>
            </div>
          </div>
          <button
            onClick={() => setShowGrid((prev) => !prev)}
            className="inline-flex items-center gap-2 text-sm text-primary-700 bg-primary-50 border border-primary-200 px-3 py-2 rounded-md hover:bg-primary-100"
          >
            {showGrid ? (
              <>
                <ChevronUpIcon className="h-4 w-4" />
                Nascondi griglia
              </>
            ) : (
              <>
                <ChevronDownIcon className="h-4 w-4" />
                Mostra griglia completa
              </>
            )}
          </button>
        </div>
        {showGrid && (
          <div className="border-t border-gray-200">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Proclamatore
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Assegnazioni
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Disponibilità
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {stats.volontari.length === 0 ? (
                    <tr>
                      <td
                        colSpan="3"
                        className="px-4 py-4 text-sm text-gray-500 text-center"
                      >
                        Nessun proclamatore trovato nel periodo.
                      </td>
                    </tr>
                  ) : (
                    stats.volontari.map((volontario) => (
                      <tr key={volontario.id}>
                        <td className="px-4 py-3 text-sm text-gray-900">
                          {volontario.nome} {volontario.cognome}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {volontario.assegnazioni}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700">
                          {volontario.disponibilita}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>

      {loading && (
        <div className="fixed bottom-4 right-4 bg-white border border-blue-100 shadow-lg rounded-md px-4 py-3 text-sm text-blue-800 flex items-center gap-2">
          <ArrowPathIcon className="h-4 w-4 animate-spin" />
          Aggiornamento statistiche in corso...
        </div>
      )}
    </div>
  );
};

export default CronologiaStatistiche;
